import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppSettings } from '../../proyect.conf';
import { AutenticacionService } from './autenticacion.service';
import { Garantia, GarantiaConModalidades, GarantiaModalidad } from '../_models/garantia';
import { GarantiaHistorialCambio, GarantiaHistorialCambioPayload } from '../_models/garantia-historial-cambio';
import { TipoGarantiaTree } from '../_models/tipo-garantia';

@Injectable( {
  providedIn: 'root'
} )
export class GarantiaService {
  private ruta = AppSettings.rutServiciosAPL + '/api/garantias';
  private rutaModalidades = AppSettings.rutServiciosAPL + '/api/garantiasModalidades';
  private rutaGarantiaModalidad = AppSettings.rutServiciosAPL + '/api/garantias/garantiasModalidades';
  private rutaHistorialesCambios = AppSettings.rutServiciosAPL + '/api/garantiasHistorialesCambios/';

  constructor( private httpClient: HttpClient,
               private autenticacionService: AutenticacionService ) {}

  /**
   * Obtiene el listado completo de garantías.
   * @returns Observable con arreglo de garantías
   */
  listado(): Observable<Garantia[]> {
    return this.httpClient.get( this.ruta ).pipe( map( res => res as Garantia[] ) );
  }

  /**
   * Obtiene una garantía por su identificador.
   * @param idGarantia Identificador único de la garantía
   */
  obtener( idGarantia: string ): Observable<Garantia> {
    return this.httpClient.get( this.ruta + idGarantia ).pipe( map( res => res as Garantia ) );
  }

  /**
   * Registra una nueva garantía.
   * @param garantia Datos de la garantía a crear
   */
  agregar( garantia: Garantia ): Observable<Garantia> {
    return this.httpClient.post( this.ruta, garantia ).pipe( map( res => res as Garantia ) );
  }

  /**
   * Actualiza una garantía existente.
   * @param garantia Datos actualizados de la garantía
   */
  actualizar( garantia: Garantia ): Observable<any> {
    return this.httpClient.put( this.ruta, garantia ).pipe( map( res => res ) );
  }

  /**
   * Obtiene garantías con sus modalidades
   * @returns Observable con arreglo de GarantiaConModalidades
   */
  listadoConModalidades(): Observable<GarantiaConModalidades[]> {
    return this.httpClient.get( this.rutaGarantiaModalidad ).pipe( map( res => res as GarantiaConModalidades[] ) );
  }

  /**
   * Obtiene todos los indicadores de modalidad de garantía.
   */
  listadoModalidades(): Observable<GarantiaModalidad[]> {
    return this.httpClient.get( this.rutaModalidades ).pipe( map( res => res as GarantiaModalidad[] ) );
  }

  /**
   * Almacena en bulk los indicadores de modalidad para una garantía.
   */
  agregarModalidadesBulk(
    modalidades: Pick<GarantiaModalidad, 'idGarantia' | 'idModalidadGarantia' | 'indicadorHabilitado'>[]
  ): Observable<any> {
    return this.httpClient.post( this.rutaModalidades + '/bulk', modalidades );
  }

  /**
   * Obtiene el historial de cambios habilitados de una garantía.
   * @param idGarantia Identificador UUID de la garantía
   */
  listadoHistorialesCambios( idGarantia: string ): Observable<GarantiaHistorialCambio[]> {
    return this.httpClient
      .get( `${this.rutaHistorialesCambios}garantia/${idGarantia}/enabled` )
      .pipe( map( res => res as GarantiaHistorialCambio[] ) );
  }

  /**
   * Registra en bulk los cambios realizados sobre los campos de una garantía
   */
  registrarHistorialCambiosBulk( cambios: GarantiaHistorialCambioPayload[] ): Observable<any> {
    return this.httpClient.post( `${this.rutaHistorialesCambios}bulk`, cambios );
  }

  /**
   * Obtiene las modalidades de garantía habilitadas por idLista.
   * @param idLista Identificador de la lista
   */
  obtenerModalidadesHabilitadasPorIdModalidadGarantia( idModalidadGarantia: string ): Observable<GarantiaModalidad[]> {
    return this.httpClient
      .get( `${this.rutaModalidades}/idModalidadGarantia/${idModalidadGarantia}/enabled` )
      .pipe( map( res => res as GarantiaModalidad[] ) );
  }

  /**
   * Obtiene el árbol de tipos de garantía por modalidad.
   * @param idModalidad Identificador de la modalidad (idLista)
   */
  obtenerArbolPorModalidad( idModalidad: string ): Observable<TipoGarantiaTree[]> {
    return this.httpClient
      .get( `${this.rutaModalidades}/modalidad/${idModalidad}/enabled` )
      .pipe( map( res => res as TipoGarantiaTree[] ) );
  }

  envioNotification( idGarantia: string ): Observable<any> {
    const idUsuario = this.autenticacionService.obtenerIdUsuarioLogueado();
    return this.httpClient.get( `${this.ruta}/envioNotification/garantiaInhabilitada/${idUsuario}/${idGarantia}` );
  }
}
